import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Activity, Users, Clock, Eye, LogIn, LogOut, Circle, RefreshCw,
  User, ChevronRight, Calendar, Timer, Wifi, WifiOff
} from 'lucide-react';
import axios from 'axios';

const API = '/api';

const formatDuration = (seconds) => {
  if (!seconds || seconds < 0) return '0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const formatDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }) + ' ' +
    d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const timeAgo = (iso) => {
  if (!iso) return '-';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'agora';
  if (diff < 3600) return `${Math.floor(diff / 60)}m atras`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h atras`;
  return `${Math.floor(diff / 86400)}d atras`;
};

const roleLabel = (r) => ({
  super_admin: 'Super Admin', admin: 'Admin', supervisor: 'Supervisor',
  leader: 'Lider', pilot: 'Piloto', vigilant: 'Vigilante'
}[r] || r);

const roleColor = (r) => ({
  super_admin: 'text-gold', admin: 'text-blue-600', supervisor: 'text-purple-600',
  leader: 'text-cyan-600', pilot: 'text-green-600', vigilant: 'text-gray-600'
}[r] || 'text-gray-500');

const ActivityPage = () => {
  const { token } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const loadData = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/activity/all`, { headers });
      setActivities(r.data);
    } catch {} finally { setLoading(false); }
  }, [token]);

  useEffect(() => { loadData(); const i = setInterval(loadData, 30000); return () => clearInterval(i); }, [loadData]);

  const viewSessions = async (user) => {
    setSelectedUser(user);
    try {
      const r = await axios.get(`${API}/activity/sessions/${user.user_id}`, { headers });
      setSessions(r.data);
    } catch { setSessions([]); }
    setSessionsOpen(true);
  };

  const onlineCount = activities.filter(a => a.is_online).length;
  const totalVisits = activities.reduce((s, a) => s + (a.total_visits || 0), 0);
  const totalTime = activities.reduce((s, a) => s + (a.total_time_seconds || 0), 0);

  return (
    <div className="space-y-6" data-testid="activity-page">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { icon: Wifi, label: 'Online Agora', value: onlineCount, color: 'text-green-600 bg-green-50 border-green-200' },
          { icon: Users, label: 'Usuarios Monitorados', value: activities.length, color: 'text-blue-600 bg-blue-50 border-blue-200' },
          { icon: Eye, label: 'Total de Visitas', value: totalVisits, color: 'text-purple-600 bg-purple-50 border-purple-200' },
          { icon: Timer, label: 'Tempo Total Acumulado', value: formatDuration(totalTime), color: 'text-gold bg-amber-50 border-amber-200' },
        ].map(s => (
          <div key={s.label} className={`rounded-xl border p-4 ${s.color}`} data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center gap-2 mb-2">
              <s.icon size={16} />
              <span className="text-[10px] font-mono uppercase tracking-wider opacity-70">{s.label}</span>
            </div>
            <p className="text-2xl font-rajdhani font-bold">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Activity Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Activity size={16} className="text-gold" />
            <h3 className="text-navy text-sm font-rajdhani font-semibold uppercase tracking-wider">Atividade dos Usuarios</h3>
          </div>
          <Button variant="ghost" size="sm" onClick={loadData} className="text-gray-400 hover:text-navy h-8" data-testid="refresh-btn">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>

        <ScrollArea className="max-h-[60vh]">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="activity-table">
              <thead className="bg-gray-50 text-[10px] font-mono uppercase text-gray-500 sticky top-0">
                <tr>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Usuario</th>
                  <th className="text-left p-3 hidden sm:table-cell">Papel</th>
                  <th className="text-left p-3 hidden md:table-cell">Ultima Atividade</th>
                  <th className="text-right p-3">Visitas</th>
                  <th className="text-right p-3 hidden sm:table-cell">Tempo Total</th>
                  <th className="text-right p-3">Detalhes</th>
                </tr>
              </thead>
              <tbody>
                {activities.map(a => (
                  <tr key={a.user_id} className="border-t border-gray-50 hover:bg-gray-50/50 transition-colors" data-testid={`activity-row-${a.username}`}>
                    <td className="p-3">
                      {a.is_online ? (
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-green-50 border border-green-200 text-green-700 text-[10px] font-semibold">
                          <Circle size={6} className="fill-green-500 text-green-500 animate-pulse" /> ONLINE
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-gray-100 border border-gray-200 text-gray-500 text-[10px] font-semibold">
                          <Circle size={6} className="fill-gray-400 text-gray-400" /> OFFLINE
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-navy/5 flex items-center justify-center flex-shrink-0">
                          <User size={12} className="text-navy" />
                        </div>
                        <div>
                          <p className="text-navy font-medium text-xs leading-tight">{a.name || a.username}</p>
                          <p className="text-gray-400 text-[9px] font-mono">@{a.username}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 hidden sm:table-cell">
                      <span className={`text-[10px] font-semibold uppercase ${roleColor(a.role)}`}>{roleLabel(a.role)}</span>
                    </td>
                    <td className="p-3 hidden md:table-cell">
                      <span className="text-gray-500 text-xs">{timeAgo(a.last_seen)}</span>
                    </td>
                    <td className="p-3 text-right">
                      <span className="text-navy font-mono font-bold text-xs">{a.total_visits || 0}</span>
                    </td>
                    <td className="p-3 text-right hidden sm:table-cell">
                      <span className="text-navy font-mono text-xs">{formatDuration(a.total_time_seconds)}</span>
                    </td>
                    <td className="p-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => viewSessions(a)}
                        className="text-gray-400 hover:text-navy h-7 px-2" data-testid={`view-sessions-${a.username}`}>
                        <ChevronRight size={14} />
                      </Button>
                    </td>
                  </tr>
                ))}
                {activities.length === 0 && !loading && (
                  <tr><td colSpan={7} className="text-center py-8 text-gray-400 text-xs font-mono">Nenhuma atividade registrada</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </ScrollArea>
      </div>

      {/* Sessions Dialog */}
      <Dialog open={sessionsOpen} onOpenChange={setSessionsOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[70vh]">
          <DialogHeader>
            <DialogTitle className="text-navy font-rajdhani uppercase tracking-wider flex items-center gap-2">
              <Clock size={16} className="text-gold" /> Historico - {selectedUser?.name || selectedUser?.username}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[50vh] mt-4">
            <div className="space-y-1.5">
              {sessions.map((s, i) => (
                <div key={i} className={`flex items-center gap-3 px-3 py-2 rounded-lg ${s.type === 'login' ? 'bg-green-50 border border-green-100' : 'bg-red-50 border border-red-100'}`}>
                  {s.type === 'login' ? <LogIn size={12} className="text-green-600" /> : <LogOut size={12} className="text-red-500" />}
                  <span className={`text-xs font-semibold ${s.type === 'login' ? 'text-green-700' : 'text-red-600'}`}>
                    {s.type === 'login' ? 'LOGIN' : 'LOGOUT'}
                  </span>
                  <span className="text-gray-500 text-[11px] font-mono ml-auto">{formatDate(s.timestamp)}</span>
                </div>
              ))}
              {sessions.length === 0 && (
                <p className="text-gray-400 text-xs text-center py-4 font-mono">Nenhum registro</p>
              )}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ActivityPage;
